import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import DynamicSidebar from '../../components/ui/DynamicSidebar';
import DashboardHeader from '../../components/ui/DashboardHeader';
import Icon from '../../components/AppIcon';

const ApiKeysPage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { user, organization, businessCode: contextBusinessCode } = useAuth();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resettingId, setResettingId] = useState(null);
  const [visibleSecrets, setVisibleSecrets] = useState({});
  const [visibleKeys, setVisibleKeys] = useState({});
  const [confirmReset, setConfirmReset] = useState(null); // merchant_id to confirm
  const [otpStep, setOtpStep] = useState(false); // whether we're on OTP step
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');

  useEffect(() => {
    const bc =
      contextBusinessCode ||
      user?.business_code ||
      organization?.business_code ||
      localStorage.getItem('dp_business_code') ||
      '';
    if (bc) fetchApiKeys(bc);
  }, [contextBusinessCode, user, organization]);

  const fetchApiKeys = async (bc) => {
    setLoading(true);
    setError('');
    try {
      const response = await apiService?.getApiKeys(bc);
      const details = response?.data?.details || [];
      setApiKeys(details);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load API credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (merchantId) => {
    setResettingId(merchantId);
    setError('');
    setOtpError('');
    try {
      const response = await apiService?.regenerateApiKeys(merchantId);
      const newDetails = response?.data?.details || [];
      if (newDetails?.length > 0) {
        setApiKeys(prev =>
          prev?.map(key =>
            key?.merchant_id === merchantId ? { ...key, ...newDetails?.[0] } : key
          )
        );
      }
      setSuccess(`API credentials for ${merchantId} reset successfully!`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to reset API credentials');
    } finally {
      setResettingId(null);
      setConfirmReset(null);
      setOtpStep(false);
      setOtp('');
    }
  };

  const handleConfirmProceed = () => {
    // Move from confirmation step to OTP step
    setOtpStep(true);
    setOtp('');
    setOtpError('');
  };

  const handleOtpSubmit = async () => {
    if (!otp || otp?.trim()?.length === 0) {
      setOtpError('Please enter your OTP.');
      return;
    }
    if (otp?.trim()?.length < 4) {
      setOtpError('OTP must be at least 4 digits.');
      return;
    }
    setResettingId(confirmReset);
    setOtpError('');
    try {
      const emailAddress = user?.email_address || user?.email || '';
      const verifyResponse = await apiService?.verify2FA({ email_address: emailAddress, code: otp?.trim() });
      const verifyData = verifyResponse?.data;
      const verified = verifyData?.details?.verified;
      if (verifyData?.status_code === 200 && verified === true) {
        await handleReset(confirmReset);
      } else {
        setOtpError(verifyData?.message || 'Invalid OTP. Please try again.');
        setResettingId(null);
      }
    } catch (err) {
      setOtpError(err?.response?.data?.message || 'OTP verification failed. Please try again.');
      setResettingId(null);
    }
  };

  const handleCancelReset = () => {
    setConfirmReset(null);
    setOtpStep(false);
    setOtp('');
    setOtpError('');
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard?.writeText(text)?.then(() => {
      setSuccess(`${label} copied!`);
      setTimeout(() => setSuccess(''), 2000);
    });
  };

  const maskValue = (value) => {
    if (!value) return '—';
    if (value?.length <= 8) return '••••••••';
    return value?.slice(0, 4) + '••••••••' + value?.slice(-4);
  };

  const formatExpiry = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr)?.toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const toggleKeyVisibility = (merchantId) => {
    setVisibleKeys(prev => ({ ...prev, [merchantId]: !prev?.[merchantId] }));
  };

  const toggleSecretVisibility = (merchantId) => {
    setVisibleSecrets(prev => ({ ...prev, [merchantId]: !prev?.[merchantId] }));
  };

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader onMenuToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <DynamicSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        <div className="p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-text-primary">API Credentials</h1>
            <p className="text-text-secondary mt-1">Manage your API keys for integration</p>
          </div>

          {/* Alerts */}
          {error && (
            <div className="mb-4 flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg max-w-4xl">
              <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}
          {success && (
            <div className="mb-4 flex items-center space-x-2 p-3 bg-success/10 border border-success/20 rounded-lg max-w-4xl">
              <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0" />
              <p className="text-sm text-success">{success}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Icon name="Loader2" size={32} className="animate-spin text-primary" />
            </div>
          ) : apiKeys?.length > 0 ? (
            <div className="bg-surface border border-border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50 border-b border-border">
                      <th className="text-left px-4 py-3 font-semibold text-text-secondary whitespace-nowrap">Merchant ID</th>
                      <th className="text-left px-4 py-3 font-semibold text-text-secondary whitespace-nowrap">Consumer Key</th>
                      <th className="text-left px-4 py-3 font-semibold text-text-secondary whitespace-nowrap">Consumer Secret</th>
                      <th className="text-left px-4 py-3 font-semibold text-text-secondary whitespace-nowrap">Expiry</th>
                      <th className="text-left px-4 py-3 font-semibold text-text-secondary whitespace-nowrap">Status</th>
                      <th className="text-left px-4 py-3 font-semibold text-text-secondary whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiKeys?.map((key, idx) => (
                      <tr key={key?.merchant_id || idx} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors">
                        {/* Merchant ID */}
                        <td className="px-4 py-3">
                          <span className="font-mono font-medium text-text-primary">{key?.merchant_id || '—'}</span>
                        </td>

                        {/* Consumer Key */}
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-2">
                            <code className="font-mono text-xs text-text-primary bg-muted px-2 py-1 rounded max-w-[180px] truncate block">
                              {visibleKeys?.[key?.merchant_id] ? key?.consumer_key : maskValue(key?.consumer_key)}
                            </code>
                            <button
                              onClick={() => toggleKeyVisibility(key?.merchant_id)}
                              className="p-1 rounded hover:bg-muted text-text-secondary flex-shrink-0"
                              title={visibleKeys?.[key?.merchant_id] ? 'Hide' : 'Show'}
                            >
                              <Icon name={visibleKeys?.[key?.merchant_id] ? 'EyeOff' : 'Eye'} size={14} />
                            </button>
                            <button
                              onClick={() => copyToClipboard(key?.consumer_key, 'Consumer Key')}
                              className="p-1 rounded hover:bg-muted text-text-secondary flex-shrink-0"
                              title="Copy"
                            >
                              <Icon name="Copy" size={14} />
                            </button>
                          </div>
                        </td>

                        {/* Consumer Secret */}
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-2">
                            <code className="font-mono text-xs text-text-primary bg-muted px-2 py-1 rounded max-w-[140px] truncate block">
                              {visibleSecrets?.[key?.merchant_id] ? key?.consumer_secret : maskValue(key?.consumer_secret)}
                            </code>
                            <button
                              onClick={() => toggleSecretVisibility(key?.merchant_id)}
                              className="p-1 rounded hover:bg-muted text-text-secondary flex-shrink-0"
                              title={visibleSecrets?.[key?.merchant_id] ? 'Hide' : 'Show'}
                            >
                              <Icon name={visibleSecrets?.[key?.merchant_id] ? 'EyeOff' : 'Eye'} size={14} />
                            </button>
                            <button
                              onClick={() => copyToClipboard(key?.consumer_secret, 'Consumer Secret')}
                              className="p-1 rounded hover:bg-muted text-text-secondary flex-shrink-0"
                              title="Copy"
                            >
                              <Icon name="Copy" size={14} />
                            </button>
                          </div>
                        </td>

                        {/* Expiry */}
                        <td className="px-4 py-3 whitespace-nowrap text-text-secondary">
                          {formatExpiry(key?.credential_expiry)}
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            key?.status === 1
                              ? 'bg-success/10 text-success' :'bg-error/10 text-error'
                          }`}>
                            {key?.status === 1 ? 'Active' : 'Inactive'}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setConfirmReset(key?.merchant_id)}
                            disabled={resettingId === key?.merchant_id}
                            className="flex items-center space-x-1.5 px-3 py-1.5 border border-warning text-warning rounded-lg text-xs font-medium hover:bg-warning/10 disabled:opacity-50 transition-colors"
                          >
                            {resettingId === key?.merchant_id ? (
                              <><Icon name="Loader2" size={12} className="animate-spin" /><span>Resetting...</span></>
                            ) : (
                              <><Icon name="RefreshCw" size={12} /><span>Reset</span></>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (contextBusinessCode || user?.business_code || organization?.business_code || localStorage.getItem('dp_business_code')) ? (
            <div className="text-center py-16 bg-surface border border-border rounded-xl">
              <Icon name="Key" size={36} className="text-text-secondary mx-auto mb-3" />
              <p className="text-text-secondary">No API credentials found</p>
            </div>
          ) : null}
        </div>
      </main>

      {/* Confirm Reset Modal */}
      {confirmReset && !otpStep && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface border border-border rounded-xl p-6 max-w-sm w-full shadow-lg">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-warning/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" size={20} className="text-warning" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary">Reset API Credentials?</h3>
            </div>
            <p className="text-sm text-text-secondary mb-2">
              This will generate new credentials for merchant <span className="font-mono font-semibold text-text-primary">{confirmReset}</span>.
            </p>
            <p className="text-sm text-text-secondary mb-6">
              ⚠️ Current credentials will be invalidated immediately.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={handleCancelReset}
                className="flex-1 px-4 py-2 border border-border text-text-primary rounded-lg text-sm hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmProceed}
                className="flex-1 px-4 py-2 bg-warning text-white rounded-lg text-sm font-medium hover:bg-warning/90 flex items-center justify-center"
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* OTP Verification Modal */}
      {confirmReset && otpStep && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface border border-border rounded-xl p-6 max-w-sm w-full shadow-lg">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="ShieldCheck" size={20} className="text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary">Verify OTP</h3>
            </div>
            <p className="text-sm text-text-secondary mb-4">
              Enter the OTP from authenticator to confirm resetting credentials for merchant <span className="font-mono font-semibold text-text-primary">{confirmReset}</span>.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text-primary mb-1.5">One-Time Password (OTP)</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={8}
                value={otp}
                onChange={(e) => {
                  setOtp(e?.target?.value?.replace(/\D/g, ''));
                  setOtpError('');
                }}
                placeholder="Enter OTP"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary tracking-widest text-center"
                autoFocus
              />
              {otpError && (
                <p className="mt-1.5 text-xs text-error flex items-center space-x-1">
                  <Icon name="AlertCircle" size={12} />
                  <span>{otpError}</span>
                </p>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleCancelReset}
                className="flex-1 px-4 py-2 border border-border text-text-primary rounded-lg text-sm hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={handleOtpSubmit}
                disabled={resettingId === confirmReset}
                className="flex-1 px-4 py-2 bg-warning text-white rounded-lg text-sm font-medium hover:bg-warning/90 disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                {resettingId === confirmReset ? (
                  <><Icon name="Loader2" size={14} className="animate-spin" /><span>Resetting...</span></>
                ) : (
                  <span>Confirm Reset</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeysPage;
